import { atomWithSuspenseQuery } from "jotai-tanstack-query";
import { queryClient } from "../queryClient";
import { MANUAL, LATEST } from "../queryCachePolicy";
import { accessTokenInfoAtom } from "../store";
import { AccessToken } from "../types";
import type { components } from "../types/voislog_api";
import { voislogApi } from "../utils/apiClients/voislogApi";

/** 読み上げ文設定のクエリキー。ユーザー別キャッシュの無効化は先頭要素のみ指定する */
export const PHRASE_SETTING_KEY = "phraseSetting";

/** 録音で読み上げる文のクエリキー。ユーザー別キャッシュの破棄は先頭要素のみ指定する */
export const RECORDING_PHRASES_KEY = "recordingPhrases";

type RecordingPhrasesQuery = {
  queryKey: string[];
  queryFn: () => Promise<components["schemas"]["PhrasePickResponse"]>;
};

// 録音で読み上げる文の一覧と最少発話数
function recordingPhrasesQuery(
  tokenInfo: AccessToken | null,
): RecordingPhrasesQuery {
  return {
    queryKey: [RECORDING_PHRASES_KEY, tokenInfo?.access_token ?? ""],
    queryFn: async () => {
      const { data } = await voislogApi.GET(
        "/v2/v_management/app_settings/phrase/pick",
        { params: { query: { user_id: tokenInfo!.user_id } } },
      );
      return data!;
    },
  };
}

// 録音画面では取り直さず、先読みしたものを使う。先読みが無ければここで取得する
// atom は閉じている間も前回の状態を持ち越すので、録音画面は開くたびにキャッシュを読み直す useSuspenseQuery で使う
export function recordingPhrasesSuspenseQuery(
  tokenInfo: AccessToken | null,
): RecordingPhrasesQuery & typeof MANUAL {
  return { ...recordingPhrasesQuery(tokenInfo), ...MANUAL };
}

// ホーム画面に来るたびに、次の録音で読み上げる文を取り直しておく
export function prefetchRecordingPhrases(
  tokenInfo: AccessToken,
): Promise<void> {
  return queryClient.prefetchQuery({
    ...recordingPhrasesQuery(tokenInfo),
    ...LATEST,
  });
}

// 読み上げ文設定
export const phraseSettingQueryAtom = atomWithSuspenseQuery(
  (get) => {
    const tokenInfo = get(accessTokenInfoAtom);
    return {
      queryKey: [PHRASE_SETTING_KEY, tokenInfo?.access_token ?? ""],
      queryFn: async () => {
        const { data } = await voislogApi.GET(
          "/v2/v_management/app_settings/phrase",
          { params: { query: { user_id: tokenInfo!.user_id } } },
        );
        return data!;
      },
      ...LATEST,
    };
  },
  () => queryClient,
);
